import styled, { css } from 'styled-components';

import { getDefaultForegroundColor, getDefaultImageBackground, getDefaultImagePlaceholderColor } from '@mews-ui/theme';
import { ANIMATION_DURATION, BORDER_RADIUS } from '@optimus-web/local-tokens';
import { Icon } from '../Icon';
import { ImageProps, ImageRatio, PlaceholderProps } from './ImageProps';

export const placeholderIconSize = 40;

interface WithRatio {
    isRatioDefined: boolean;
}

export interface ImageElementProps extends WithRatio {
    isLoaded: boolean;
    shouldShowLoader?: boolean;
    hidden?: boolean;
    grayscale?: boolean;
    objectFit?: 'contain' | 'cover';
    objectPosition?: string;
    rounded?: boolean;
    block?: boolean;
}

export const ImageElement = styled.img<ImageElementProps>`
    background: ${(props) => (props.isLoaded ? getDefaultImageBackground(props) : getDefaultForegroundColor(props))};
    opacity: ${(props) => (props.isLoaded ? 1 : 0)};
    transition: ${ANIMATION_DURATION.default}ms opacity ease-out;
    max-width: 100%;
    ${(props) =>
        (props.shouldShowLoader && !props.isLoaded) || props.hidden
            ? css`
                  visibility: hidden;
                  height: 1px !important;
                  width: 1px !important;
              `
            : props.height && `height: ${props.height};`}
    ${(props) => props.grayscale && 'filter: grayscale(1);'}
    ${(props) => props.objectFit && `object-fit: ${props.objectFit};`}
    ${(props) => props.objectPosition && `object-position: ${props.objectPosition};`}
    ${(props) => props.rounded && `border-radius: ${BORDER_RADIUS[50]};`}

    ${(props) =>
        props.isRatioDefined &&
        css`
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
        `}

    ${(props) =>
        props.block === true
            ? css`
                  display: block;
                  width: 100%;
              `
            : undefined}
`;

type ImagePlaceholderProps = PlaceholderProps & Pick<ImageProps, 'rounded'> & WithRatio;

export const ImagePlaceholder = styled.div<ImagePlaceholderProps>`
    min-height: ${(props) => props.minHeight || placeholderIconSize}px;
    min-width: ${(props) => props.minWidth || placeholderIconSize}px;
    background-color: ${(props) =>
        props.backgroundVariant === 'transparent' ? 'transparent' : getDefaultImagePlaceholderColor(props)};
    position: relative;
    ${(props) => props.rounded && `border-radius: ${BORDER_RADIUS[50]};`}

    ${(props) =>
        props.isRatioDefined &&
        css`
            position: absolute;
            inset: 0;
        `}
`;

export const ImagePlaceholderIcon = styled(Icon)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${placeholderIconSize}px;
    height: ${placeholderIconSize}px;

    svg {
        width: ${placeholderIconSize}px;
        height: ${placeholderIconSize}px;
    }
`;

const calculateRatio = (ratio: ImageRatio) => {
    if (parseInt(ratio, 10) === 1) {
        return 100;
    }
    const arr = ratio.split('/').map((item) => parseInt(item, 10));
    return (arr[1] / arr[0]) * 100;
};

interface ImageWrapperProps {
    imageRatio?: ImageRatio;
}

export const ImageWrapper = styled.div<ImageWrapperProps>`
    position: relative;
    height: 0;
    ${(props) => (props.imageRatio ? `padding-bottom: ${calculateRatio(props.imageRatio)}%` : null)};
`;

export const CircleLoaderWrapper = styled.div<WithRatio>`
    ${(props) =>
        props.isRatioDefined &&
        css`
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
        `}
`;
